@import '../../globals/variables';

/**
 * THEME JSON CONFIG CSS VARS
 * The following default component scss variables can be overwritten via theme configuration per brand.
 * If you need to add new one, please make sure to set a default value.
 * JSON Definition for Component with name "Example": testCssVar: "20px"
 * Resulting CSS var: $example-test-css-var: 20px;
 */

// $input-height: 48px !default; // value of input, as input is used internally
// $input-small-height: 36px !default; // value of input, as input is used internally
// $input-border-width: 2px !default; // value of input, as input is used internally
// $input-small-border-width: 2px !default; // value of input, as input is used internally

$date-picker-container-radius: 4px !default;
$date-picker-container-border-width: 3px !default;
$date-picker-container-focus-border-width: 3px !default;
$date-picker-container-small-focus-border-width: 3px !default;
$date-picker-calendar-day-border-width: 2px !default;
$date-picker-calendar-day-radius: 4px !default;
$date-picker-content-background-color: v(popupBackgroundColor) !default;
$date-picker-border-color: v(popupBorderColor) !default;
$date-picker-font-color: v(fontColor) !default;
$date-picker-font-disabled-color: v(dayFontDisabledColor) !default;
$date-picker-calendar-day-hover-color: v(dayBorderHoverColor) !default;
$date-picker-calendar-day-highlight-color: v(dayBorderSelectedColor) !default;
$date-picker-calendar-day-today-color: v(dayBackgroundTodayColor) !default;
$date-picker-breakpoint-small: 275px;
$date-picker-breakpoint-medium: 603px;
$date-picker-max-width: 723px;

/* END THEME JSON CONFIG CSS VARS ******************************************************* */

@mixin datePickerButton {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  //color: $date-picker-font-color;
  color: v(arrowButtonColor); // ENVIAM
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: $font-weight-regular;
  background-color: v(dayBackgroundColor);
  border-radius: $date-picker-calendar-day-radius;
  padding: 0;
  transition: 0.2s box-shadow, 0.2s background-color;

  &:hover:not(:disabled) {
    box-shadow: 0 0 0 2px $date-picker-calendar-day-hover-color;
    font-weight: $font-weight-bold;
  }

  &.#{$prefix}selected {
    background-color: $date-picker-calendar-day-highlight-color;
    color: $eon-white;
  }

  &:focus-visible {
    background-color: v(popupFocusBorderColor);
    box-shadow: none;
    color: $eon-white;
    outline: none;

    envc-eon-ui-icon {
      color: $eon-white;
    }
  }

  &:disabled {
    color: $date-picker-font-disabled-color;
    background-color: transparent;
    cursor: default;

    &:hover {
      border-color: transparent;
    }

    &.#{$prefix}highlight,
    &.#{$prefix}selected {
      border-color: transparent;
    }
  }
}

:host {
  display: block;
}

.#{$prefix}date-picker-wrapper * {
  user-select: none; /* Non-prefixed version, currently */
}

.#{$prefix}date-picker-wrapper {
  width: 100%;
  container-type: inline-size; /* tooltip positioning breaks with floating-ui when using shorthand property  */
  container-name: wrapper;
  contain: layout;
  position: relative;
  box-sizing: border-box;
  font-family: $font-family-base;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: $date-picker-font-color;

  button {
    font-family: inherit;
  }

  > * {
    box-sizing: border-box;
  }

  &.#{$prefix}auto-width > :first-child {
    max-width: none;
  }

  &.#{$prefix}focus {
    z-index: $mainNavigationZIndex - 1; // It must follow the tooltip implementation, otherwise it's going to overlap.

    .#{$prefix}date-picker-container {
      border-color: v(popupFocusBorderColor);
    }
  }

  .#{$prefix}date-picker-container {
    position: absolute;
    max-width: $date-picker-max-width;
    width: 100%;
    left: 0;
    top: 55px;
    height: 0;
    background: $date-picker-content-background-color;
    box-shadow: 0px 2px 24px 0px #00000033;
    border-radius: 8px;
    overflow: hidden;
    padding: 24px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 32px;
    outline: none;
    opacity: 0;
    visibility: hidden;

    @container wrapper (width > #{$date-picker-breakpoint-medium}) {
      display: grid;
      grid-template-columns: 1fr 272px;
      gap: 32px;
    }

    @container wrapper (max-width: #{$date-picker-breakpoint-small}) {
      .#{$prefix}year-selection-container-body-row {
        margin-bottom: 14px;
      }

      .#{$prefix}month-year-selection-container
        .#{$prefix}month-year-selection-body
        .#{$prefix}year-selection-container {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    }

    &.#{$prefix}position-right {
      left: unset;
      right: 0;
    }

    &.#{$prefix}open-input,
    &.#{$prefix}open-calendar {
      opacity: 1;
      visibility: visible;
      height: auto;
    }

    .#{$prefix}date-picker-container-body {
      position: relative;
      width: 100%;
      font-family: $font-family-base;
      font-size: 18px;
      font-weight: 400;
      line-height: 27px;
      color: $date-picker-font-color;
      display: flex;
      flex-direction: column;
      gap: 16px;

      button {
        font-family: inherit;
      }

      @container wrapper (min-width: $date-picker-breakpoint-medium) {
        max-width: 272px;
      }

      &-calendar {
        position: relative;
        display: block;
        box-sizing: border-box;

        &-head {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          justify-content: space-between;
          gap: 4px;
          margin-bottom: 8px;
          color: $date-picker-font-color;
          font-size: 14px;
          font-weight: 700;
          line-height: 21px;

          &-name {
            height: 32px;
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;
          }
        }

        &-body-row {
          display: grid;
          justify-content: space-between;
          grid-template-columns: repeat(7, 1fr);
          gap: 4px;
          margin-bottom: 4px;

          &:last-child {
            margin: 0;
          }
        }

        &-body-day {
          flex-shrink: 0;
          padding: 0;

          &.#{$prefix}today button {
            box-shadow: 0 0 0 1px $date-picker-calendar-day-today-color;
            color: inherit;
          }
        }

        &-body-day button {
          @include datePickerButton;
          width: 100%;
          height: 32px;

          &-text {
            align-self: center;
            line-height: 28px;
            width: 100%;
          }
        }
      }
    }

    @media (max-width: 300px) {
      @container wrapper (max-width: #{$date-picker-breakpoint-small}) {
        left: 50%;
        transform: translateX(-50%);
        width: calc(100vw - 24px);
      }
    }

    @container wrapper (max-width: #{$date-picker-breakpoint-small}) {
      padding: 12px;
      gap: 16px;

      .#{$prefix}date-picker-container-body-calendar-body-row {
        gap: 2px;
        margin-bottom: 2px;
      }
    }

    @container wrapper (min-width: #{$date-picker-breakpoint-small}) {
      .#{$prefix}date-picker-container-body-calendar-body-row {
        gap: 8px;
        margin-bottom: 8px;
      }
    }
  }

  .#{$prefix}month-year-selection-container {
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 24px;

    .#{$prefix}month-year-selection-head {
      position: relative;
      display: flex;
      justify-content: space-between;
      gap: 16px;

      .#{$prefix}month-year-toggle {
        background-color: transparent;

        // ENVIAM
        //color: v(monthYearToggleBorderColor);
        color: v(monthYearToggleBorderActiveColor);
        // END: ENVIAM

        border: none;
        position: relative;
        width: 100%;
        height: 44px;
        padding: 0px 12px 0px 12px;
        border-radius: 8px;
        box-shadow: 0 0 0 2px $date-picker-border-color;
        margin: 2px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        font-size: 18px;
        font-weight: 400;
        line-height: 27px;
        transition: 0.2s box-shadow, 0.2s color;

        &:hover {
          box-shadow: 0 0 0 3px v(monthYearToggleBorderHoverColor);
          color: $date-picker-font-color;

          envc-eon-ui-icon {
            color: v(arrowButtonColor);
          }
        }

        &:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px v(monthYearToggleBorderActiveColor);
          color: $date-picker-font-color;

          envc-eon-ui-icon {
            color: v(arrowButtonColor);
          }
        }

        &.#{$prefix}active {
          background-color: v(monthYearToggleBackgroundActiveColor);
          box-shadow: 0 0 0 3px v(monthYearToggleBorderActiveColor);
          color: $date-picker-font-color;

          > span {
            transform: rotate(180deg);
          }

          envc-eon-ui-icon {
            color: v(arrowButtonColor);
          }
        }

        envc-eon-ui-icon {
          color: v(arrowButtonColor);
        }
      }
    }

    @container wrapper (max-width: #{$date-picker-breakpoint-small}) {
      .#{$prefix}month-year-selection-head {
        gap: 8px;
      }
    }

    @container wrapper (max-width: 400px) {
      .#{$prefix}month-year-selection-head {
        flex-direction: column;

        .#{$prefix}month-year-toggle.#{$prefix}month,
        .#{$prefix}month-year-toggle.#{$prefix}year {
          width: auto;
        }
      }
    }

    @container wrapper (width > #{$date-picker-breakpoint-medium}) {
      .#{$prefix}month-year-selection-head {
        display: grid;
        grid-template-columns: 1fr 120px;
      }
    }

    .#{$prefix}month-year-selection-body {
      position: relative;
      box-sizing: border-box;
      display: none;

      &.#{$prefix}active {
        display: block;
      }

      > * {
        box-sizing: border-box;
      }

      .#{$prefix}month-selection-container {
        position: relative;
        width: 100%;
        height: auto;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        column-gap: 6px;
        row-gap: 8px;
        display: none;

        &.#{$prefix}active {
          display: grid;
        }
      }

      .#{$prefix}year-selection-container {
        position: relative;
        width: 100%;
        min-height: 36px;
        max-height: 164px;
        overflow: hidden;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        column-gap: 6px;
        row-gap: 8px;
        padding-right: 6px;
        margin-left: -2px;
        padding-left: 2px;
        margin-top: -2px;
        padding-top: 2px;
        display: none;

        @container wrapper (min-width: 550px) {
          height: 258px;
        }

        &.#{$prefix}active {
          display: grid;
        }

        &::-webkit-scrollbar {
          -webkit-appearance: none;
          background-color: v(scrollbarBackgroundColor); // #f6f6f7;
          border-radius: 4px;
          width: 8px;
        }

        &::-webkit-scrollbar-thumb {
          border-radius: 4px;
          background-color: v(scrollbarThumbColor); // #8f9192;
          -webkit-box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
        }
      }

      .#{$prefix}month-year-button {
        @include datePickerButton;
        height: 32px;

        &.#{$prefix}today {
          box-shadow: 0 0 0 1px $date-picker-calendar-day-today-color;
        }
      }
    }
  }

  .#{$prefix}date-picker-container-footer {
    position: relative;
    height: 48px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .#{$prefix}date-picker-nav-button {
      @include datePickerButton;
      width: 32px;
      height: 32px;

      envc-eon-ui-icon {
        color: v(arrowButtonColor); // ENVIAM
      }
    }

    .#{$prefix}date-picker-current-month-year {
      text-align: center;
    }
  }
}

.#{$prefix}date-picker-wrapper {
  &.#{$prefix}label-outside {
    .#{$prefix}date-picker-container {
      top: 78px;
    }
  }

  &.#{$prefix}small {
    .#{$prefix}date-picker-container {
      top: 42px;
    }

    &.#{$prefix}label-outside {
      .#{$prefix}date-picker-container {
        top: 65px;
      }
    }

    .#{$prefix}date-picker-container {
      border-width: $date-picker-container-small-focus-border-width;
    }

    .#{$prefix}month-year-selection-container .#{$prefix}month-year-selection-head .#{$prefix}month-year-toggle {
      height: 32px;
      font-size: 16px;
    }
  }
}
